/**
 * Command Router
 * Routes text/voice input to appropriate handlers
 * Implements noise handling and intent detection
 */

const COMMAND_PREFIX = '/';

const commands = {
  help: {
    description: 'Show available commands',
    handler: () => ({ type: 'help' }),
  },
  new: {
    description: 'Start a new conversation',
    handler: () => ({ type: 'action', action: 'new-chat' }),
  },
  parallel: {
    description: 'Toggle parallel mode',
    handler: () => ({ type: 'action', action: 'toggle-parallel' }),
  },
  settings: {
    description: 'Open settings',
    handler: () => ({ type: 'action', action: 'settings' }),
  },
  clear: {
    description: 'Clear current conversation',
    handler: () => ({ type: 'action', action: 'clear' }),
  },
  model: {
    description: 'Switch AI model (e.g., /model claude)',
    handler: (args) => ({ type: 'action', action: 'switch-model', model: args }),
  },
  export: {
    description: 'Export conversations',
    handler: () => ({ type: 'action', action: 'export-data' }),
  },
  voice: {
    description: 'Toggle voice input',
    handler: () => ({ type: 'action', action: 'toggle-voice' }),
  },
};

/**
 * Route user input to the appropriate handler
 * @param {string} input - User text input
 * @returns {{ type: string, action?: string, message?: string } | null}
 */
export function routeInput(input) {
  const trimmed = input.trim();

  // Command routing
  if (trimmed.startsWith(COMMAND_PREFIX)) {
    const parts = trimmed.slice(1).split(/\s+/);
    const cmd = parts[0]?.toLowerCase();
    const args = parts.slice(1).join(' ');

    const command = commands[cmd];
    if (command) {
      return command.handler(args);
    }

    // Unknown command - show help
    return {
      type: 'help',
      message: `Unknown command: /${cmd}`,
    };
  }

  // Regular message
  return null;
}

/**
 * Get help text for all available commands
 */
export function getCommandHelp() {
  return Object.entries(commands)
    .map(([name, cmd]) => `/${name} — ${cmd.description}`)
    .join('\n');
}

/**
 * Filter noise from voice input
 * @param {string} transcript
 * @returns {string | null} Cleaned text or null if noise
 */
export function filterNoise(transcript) {
  const cleaned = transcript.trim();

  // Too short
  if (cleaned.length < 2) return null;

  // Common noise patterns
  const noisePatterns = [
    /^(um|uh|hmm|ah|oh|like|so|well|okay|ok)$/i,
    /^(you know|I mean|basically)$/i,
  ];

  if (noisePatterns.some(p => p.test(cleaned))) return null;

  return cleaned;
}
